import { useEffect, useState, useRef, useMemo } from "react";
import { createRoot, type Root } from "react-dom/client";
import { Show } from "@refinedev/antd";
import { useGetIdentity, useOne } from "@refinedev/core";
import { useParams, useNavigate, Link } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Card,
  Spin,
  Button,
  Breadcrumb,
  message,
  Input,
  Row,
  Col,
  Alert,
  Collapse,
  Table,
  Tag,
  Space,
  Segmented,
  DatePicker,
} from "antd";
import {
  ArrowLeftOutlined,
  HomeOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  ClearOutlined,
  ReloadOutlined,
  ExportOutlined,
  CloudDownloadOutlined,
  AimOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";

const { Search } = Input;
import L from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import type { PlanningGroupDetail } from "../../types/planning-group";
import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES, isEurosemillasScoped } from "../../types/auth";
import { CustomMap } from "../../components/maps";
import { apiClient } from "../../services/api/apiClient";
import { downloadAuditBundle } from "../../services/api/auditBundleService";
import { usePageTitle } from "../../hooks";
import { formatPhone, phoneToTelHref } from "../../utils/phone";
import { AssignAuditorModal } from "./components/AssignAuditorModal";

const { RangePicker } = DatePicker;

// Audit-state filter buckets. We classify by status_of_audit (the
// normalized string field on the model) rather than the legacy
// audit_state__name so the chip set stays stable across regions.
type StateBucket = "planned" | "performed" | "canceled" | "unassigned";

const STATE_BUCKETS: { value: StateBucket; label: string; color: string }[] = [
  { value: "unassigned", label: "Sin asignar", color: "#fa8c16" },
  { value: "planned", label: "Planificada", color: "#1890ff" },
  { value: "performed", label: "Realizada", color: "#52c41a" },
  { value: "canceled", label: "Cancelada", color: "#bfbfbf" },
];

// Bucket assignment for the chip filter.
//
// Source of truth is the master_data audit_state name (Planificada
// / Finalizada / Realizada con Informe / Cancelada / …) — that's the
// value the rest of the UI surfaces (state tag, table column, …)
// after the cancel/finalize flows started flipping the FK. The
// legacy `status_of_audit` CharField is checked as a last-ditch
// fallback for old data that never had its FK migrated.
//
// Priority of buckets:
//   1. canceled  → state contains "cancel" (or status_of_audit == 'canceled')
//   2. performed → state contains "realiz"/"finaliz", or start_date set
//   3. unassigned → no auditor_user_id (and not in cancelled/performed)
//   4. planned   → everything else (Planificada / pending)
const classifyAudit = (props: any): StateBucket => {
  const stateName = (props?.audit_state || "").toString().toLowerCase();
  const legacyStatus = props?.status_of_audit;
  if (legacyStatus === "canceled" || stateName.includes("cancel")) {
    return "canceled";
  }
  if (
    legacyStatus === "performed" ||
    stateName.includes("realiz") ||
    stateName.includes("finaliz") ||
    props?.start_date
  ) {
    return "performed";
  }
  if (props?.auditor_user_id == null) return "unassigned";
  return "planned";
};

// Extend Leaflet types for MarkerClusterGroup
declare module 'leaflet' {
  function markerClusterGroup(options?: any): any;
}

/**
 * React-rendered popup body for an audit marker.
 *
 * Lives inside a Leaflet popup that's bound to a marker via
 * `marker.bindPopup(node)` — see the cluster-building effect. Stays a
 * tiny component so we don't drag react-i18next contexts into the
 * leaflet popup tree (we pass `t` as a prop instead — antd's
 * DatePicker still works because its locale is set globally).
 */
interface AuditMarkerPopupProps {
  props: any;
  onPlan: (next: Dayjs | null) => Promise<boolean>;
  onOpenDetail: () => void;
  onDownloadBundle: () => Promise<void>;
  // i18next's TFunction has a complex overloaded type that doesn't
  // narrow well when passed via prop — accept any callable.
  t: (key: string, fallback?: string) => any;
}

const AuditMarkerPopup: React.FC<AuditMarkerPopupProps> = ({
  props,
  onPlan,
  onOpenDetail,
  onDownloadBundle,
  t,
}) => {
  // Local busy state for the bundle download so we can show the
  // spinner on the button itself without leaking it elsewhere.
  const [bundling, setBundling] = useState(false);
  const handleDownload = async () => {
    setBundling(true);
    try {
      await onDownloadBundle();
    } finally {
      setBundling(false);
    }
  };
  // Gate the button by audit type on the client — backend rejects
  // unsupported types with 400 anyway.
  const SUPPORTED_TYPES = [1, 2, 3, 5];
  const bundleSupported = SUPPORTED_TYPES.includes(props.audit_type_id ?? -1);
  // Stored value = lo último guardado en backend. Draft value = lo
  // que se está editando en el form. Sólo persistimos al darle a
  // "Guardar" (botón submit) — antes era auto-save y resultaba fácil
  // tocar la fecha sin querer.
  const persisted = props.planning_date ? dayjs(props.planning_date) : null;
  const [planning, setPlanning] = useState<Dayjs | null>(persisted);
  const [saving, setSaving] = useState(false);

  const sameDay = (a: Dayjs | null, b: Dayjs | null) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.isSame(b, "day");
  };
  const dirty = !sameDay(planning, persisted);

  const handleSubmit = async () => {
    if (!dirty) return;
    setSaving(true);
    await onPlan(planning);
    setSaving(false);
  };

  // Chains follow the producer's ContactRole priority order. Farm chain
  // first (more specific), license chain second. Empty arrays mean
  // "nothing configured at that level".
  const farmChain: Array<any> = Array.isArray(props.contact_chains?.farm)
    ? props.contact_chains.farm
    : [];
  const licenseChain: Array<any> = Array.isArray(props.contact_chains?.license)
    ? props.contact_chains.license
    : [];
  const hasAnyChain = farmChain.length > 0 || licenseChain.length > 0;
  const bucket = classifyAudit(props);
  const bucketLabel = STATE_BUCKETS.find((b) => b.value === bucket)?.label ?? "";
  const bucketColor = STATE_BUCKETS.find((b) => b.value === bucket)?.color ?? "#999";

  const renderChain = (chain: any[], title: string, badgeColor: string) => (
    <>
      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          color: "#888",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span>{title}</span>
        <Tag color={badgeColor} style={{ margin: 0 }}>
          {chain.length}
        </Tag>
      </div>
      <div
        style={{
          marginTop: 4,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {chain.map((c, i) => (
          <div key={i} style={{ fontSize: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Tag color="default" style={{ margin: 0, minWidth: 22, textAlign: "center" }}>
                #{c.priority}
              </Tag>
              <span style={{ fontWeight: 500 }}>{c.name || "—"}</span>
              {c.position && (
                <span style={{ color: "#999" }}>· {c.position}</span>
              )}
            </div>
            <Space size={6} wrap style={{ marginLeft: 28 }}>
              {c.phone && (
                <a href={`tel:${phoneToTelHref(c.phone)}`}>
                  <PhoneOutlined /> {formatPhone(c.phone)}
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`}>
                  <MailOutlined /> {c.email}
                </a>
              )}
            </Space>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div style={{ fontFamily: "inherit" }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>
        🌾 {props.producer_name}
      </div>
      <hr style={{ margin: "6px 0", border: 0, borderTop: "1px solid #eee" }} />
      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
        <div>
          <strong>{t("audit.farm", "Finca")}:</strong> {props.farm_name}
        </div>
        <div>
          <strong>{t("audit.reference", "Referencia")}:</strong>{" "}
          {props.audit_reference}
        </div>
        <div>
          <strong>{t("audit.state", "Estado")}:</strong>{" "}
          <Tag color={bucketColor} style={{ marginInlineStart: 4 }}>
            {bucketLabel}
          </Tag>
        </div>
        {props.surface != null && (
          <div>
            <strong>{t("audit.surface", "Superficie")}:</strong>{" "}
            {Number(props.surface).toFixed(2)} ha
          </div>
        )}
        {props.auditor && props.auditor !== "N/A" && (
          <div>
            <strong>{t("audit.auditor", "Auditor")}:</strong> {props.auditor}
          </div>
        )}
        {props.start_date && (
          <div>
            <strong>
              {t("audit.completion_date", "Fecha realización")}:
            </strong>{" "}
            {dayjs(props.start_date).format("DD/MM/YYYY")}
          </div>
        )}
      </div>

      {/* Cadenas de contacto del productor (ContactRole-driven).
          Mostramos primero la cadena de la finca (más específica) y a
          continuación la de licencia. Cada contacto lleva su prioridad
          para que el coordinador sepa por dónde empezar. */}
      {farmChain.length > 0 &&
        renderChain(
          farmChain,
          t("map.contacts_farm_chain", "Cadena de la finca"),
          "blue",
        )}
      {licenseChain.length > 0 &&
        renderChain(
          licenseChain,
          t("map.contacts_license_chain", "Cadena de licencia"),
          "purple",
        )}
      {/* Sin cadena configurada: fallback al contacto del acta. */}
      {!hasAnyChain && (props.farm_contact_phone || props.farm_contact_name) && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <strong>{t("audit.contact", "Contacto")}:</strong>{" "}
          {props.farm_contact_name || "—"}
          {props.farm_contact_phone && (
            <>
              {" "}
              <a href={`tel:${phoneToTelHref(props.farm_contact_phone)}`}>
                <PhoneOutlined /> {formatPhone(props.farm_contact_phone)}
              </a>
            </>
          )}
        </div>
      )}

      {/* Planificar fecha */}
      <div style={{ marginTop: 10 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            color: "#888",
            marginBottom: 4,
          }}
        >
          {t("map.plan_for", "Planificar fecha")}
        </div>
        <Space.Compact style={{ width: "100%" }}>
          <DatePicker
            size="small"
            format="DD/MM/YYYY"
            value={planning}
            onChange={(d) => setPlanning(d)}
            disabled={saving}
            style={{ flex: 1 }}
            allowClear
            getPopupContainer={() => document.body}
          />
          <Button
            size="small"
            type="primary"
            loading={saving}
            disabled={!dirty}
            onClick={handleSubmit}
          >
            {t("common.save", "Guardar")}
          </Button>
        </Space.Compact>
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Button
          size="small"
          icon={<CloudDownloadOutlined />}
          onClick={handleDownload}
          loading={bundling}
          disabled={!bundleSupported}
          title={
            bundleSupported
              ? t("audit.download_bundle", "Descargar Excel + PDF mapa")
              : t(
                  "audit.bundle_not_supported",
                  "Solo disponible para auditorías de tipo Completa / Aforo / Localización / Trazabilidad",
                )
          }
        >
          {t("audit.bundle_short", "Excel + Mapa")}
        </Button>
        <Button size="small" type="link" onClick={onOpenDetail}>
          {t("audit.view_detail", "Ver detalle")} →
        </Button>
      </div>
    </div>
  );
};

export const PlanningGroupMap = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSpain, setIsSpain] = useState(true);
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const [mapData, setMapData] = useState<any>(null);
  const [isMapDataLoading, setIsMapDataLoading] = useState(true);
  const markerClusterGroupRef = useRef<any>(null);
  const plotsLayerRef = useRef<L.LayerGroup | null>(null);
  const routesLayerRef = useRef<L.LayerGroup | null>(null);
  const [currentZoom, setCurrentZoom] = useState(14);
  
  // Estados para el buscador
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Bottom-table search filter — independent of the top map search
  // so the planner can filter the list without nuking the cluster
  // selection. Matches against producer / farm / reference (case-
  // insensitive substring).
  const [tableSearch, setTableSearch] = useState("");

  // AssignAuditor modal — driven from the bottom-table action so
  // the coordinator can plan auditor + date without leaving the
  // map view.
  const [assignAuditorAudit, setAssignAuditorAudit] = useState<any | null>(null);

  // Filtros del planificador.
  // - stateFilters: chips activos (todos activos por defecto).
  // - dateRange: filtra por planning_date dentro de [from, to].
  // - quickRange: atajo que sobreescribe dateRange con hoy/semana/mes.
  // Cuando un filter está sin valor explícito, no se aplica restricción.
  const [stateFilters, setStateFilters] = useState<Set<StateBucket>>(
    new Set(STATE_BUCKETS.map((b) => b.value)),
  );
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [quickRange, setQuickRange] = useState<"today" | "week" | "month" | "all">(
    "all",
  );

  // Per-marker React roots so we can render an antd popup body (with
  // the planning DatePicker + click-to-call links) without rebuilding
  // the cluster from scratch. Keyed by audit id.
  const popupRootsRef = useRef<Map<number, Root>>(new Map());
  // Lookup `audit_id` → Leaflet marker so we can re-open a popup after
  // a save (which triggers a cluster rebuild that closes everything).
  const markersByAuditIdRef = useRef<Map<number, L.Marker>>(new Map());
  // The audit whose popup should re-open after the next cluster rebuild.
  // Set by the popup's save action just before it dispatches the local
  // mapData update; cleared in the cluster effect once consumed.
  const pendingPopupAuditIdRef = useRef<number | null>(null);
  // Skip auto-fit after the user starts interacting, EXCEPT when the
  // filter state changes — the planner explicitly asked us to
  // re-centre on whatever's visible after applying / clearing a
  // chip. We track the last filter signature we fit to so a
  // popup-save (which also rebuilds the cluster) doesn't re-fit.
  const didInitialFitRef = useRef(false);
  const lastFitFilterSignatureRef = useRef<string>("");

  // Effective date range: explicit RangePicker takes precedence, else
  // the quick-range buckets translate to bounds, else no constraint.
  const effectiveDateRange = useMemo<[Dayjs, Dayjs] | null>(() => {
    if (dateRange) return dateRange;
    const today = dayjs().startOf("day");
    if (quickRange === "today")
      return [today, today.endOf("day")];
    if (quickRange === "week")
      return [today.startOf("week"), today.endOf("week")];
    if (quickRange === "month")
      return [today.startOf("month"), today.endOf("month")];
    return null;
  }, [dateRange, quickRange]);

  const passesFilters = (props: any): boolean => {
    const bucket = classifyAudit(props);
    if (!stateFilters.has(bucket)) return false;
    if (effectiveDateRange) {
      const [from, to] = effectiveDateRange;
      const planned = props.planning_date ? dayjs(props.planning_date) : null;
      if (!planned) return false; // no date → out of any range
      if (planned.isBefore(from, "day") || planned.isAfter(to, "day")) return false;
    }
    return true;
  };

  // Local update so we don't have to refetch the entire map dataset
  // when we patch planning_date from a popup.
  const applyAuditPatchLocal = (auditId: number, patch: Record<string, any>) => {
    setMapData((prev: any) => {
      if (!prev?.features) return prev;
      return {
        ...prev,
        features: prev.features.map((f: any) => {
          if (f?.properties?.layer_type !== "audit_marker") return f;
          if (f.properties.audit_id !== auditId) return f;
          return {
            ...f,
            properties: { ...f.properties, ...patch },
          };
        }),
      };
    });
  };

  const updatePlanningDate = async (
    auditId: number,
    nextDate: Dayjs | null,
  ): Promise<boolean> => {
    try {
      await apiClient.patch(`/audits/${auditId}/`, {
        planning_date: nextDate ? nextDate.format("YYYY-MM-DD") : null,
      });
      // The cluster rebuild that follows the local mapData update will
      // destroy the open popup. Signal it to reopen the same one.
      pendingPopupAuditIdRef.current = auditId;
      applyAuditPatchLocal(auditId, {
        planning_date: nextDate ? nextDate.format("YYYY-MM-DD") : null,
        // Once you give it a date, the audit moves from "unassigned" to
        // "planned" — reflect it locally so the chip filter is honest.
        status_of_audit: nextDate ? "planned" : "not_planned",
      });
      message.success(
        nextDate
          ? t("map.plan_set", "Auditoría planificada")
          : t("map.plan_cleared", "Fecha quitada"),
      );
      return true;
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t("map.plan_error", "No se pudo guardar la fecha");
      message.error(detail);
      return false;
    }
  };

  // Wrapper around the shared auditBundleService that adds the
  // loading toast / success / error messaging — kept inline because
  // the toast hide/show coordination is page-specific.
  const downloadBundleFromPopup = async (markerProps: any) => {
    const auditId = markerProps?.audit_id;
    if (auditId == null) return;
    const hide = message.loading(
      t("audit.bundle_loading", "Generando paquete (Excel + PDF mapa)..."),
      0,
    );
    try {
      await downloadAuditBundle(auditId, {
        reference: markerProps.audit_reference,
      });
      message.success(
        t("audit.bundle_success", "Paquete descargado correctamente"),
      );
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t("audit.bundle_error", "No se pudo generar el paquete");
      message.error(detail);
    } finally {
      hide();
    }
  };

  const { query: planningQuery } = useOne<PlanningGroupDetail>({
    resource: "planning-groups",
    id: id!,
  });

  const planningData = planningQuery.data;
  const isPlanningLoading = planningQuery.isLoading;

  // Producer detail is admin-only — coordinators/auditors don't have
  // permissions to open it, so we hide the link/icon for them.
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin =
    (ADMIN_USER_TYPES as readonly number[]).includes(
      identity?.app_user?.user_type ?? -1,
    ) && !isEurosemillasScoped(identity);
  
  // Set page title
  usePageTitle(planningData?.data?.name ? `${t("map.title", "Mapa")} - ${planningData.data.name}` : t("map.title", "Mapa de Planificación"));

  // Determinar si el país es España
  useEffect(() => {
    if (planningData?.data?.country) {
      const countryName = planningData.data.country.name?.toLowerCase() || '';
      const countryCode = planningData.data.country.code?.toLowerCase() || '';
      setIsSpain(
        countryName === 'españa' || 
        countryName === 'spain' || 
        countryCode === 'es' ||
        countryCode === 'esp'
      );
    }
  }, [planningData]);

  // Cargar datos del mapa. Expuesto como callable para que el botón
  // de refresh de la tabla de "sin geometría" pueda re-pedirlos tras
  // editar productor/finca en otra pestaña.
  const fetchMapData = async () => {
    if (!id) return;
    try {
      setIsMapDataLoading(true);
      const response = await apiClient.get(`/planning-groups/${id}/map-data/`);
      setMapData(response.data);
    } catch {
      message.error(
        t("map.load_error", "Error al cargar datos del mapa"),
      );
    } finally {
      setIsMapDataLoading(false);
    }
  };

  useEffect(() => {
    fetchMapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Función para buscar auditorías
  const handleSearch = () => {
    if (!searchQuery.trim() || !mapData) {
      message.warning("Por favor, ingrese un término de búsqueda");
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    
    // Buscar en los marcadores de auditorías
    const auditMarkers = mapData.features.filter(
      (f: any) => f.properties.layer_type === "audit_marker"
    );

    const results = auditMarkers.filter((feature: any) => {
      const props = feature.properties;
      return (
        props.producer_name?.toLowerCase().includes(query) ||
        props.farm_name?.toLowerCase().includes(query) ||
        props.audit_reference?.toLowerCase().includes(query)
      );
    });

    if (results.length === 0) {
      message.warning("No se encontraron resultados para la búsqueda");
      setSearchResults([]);
      return;
    }

    setSearchResults(results);

    // Si solo hay un resultado, centrar directamente
    if (results.length === 1) {
      centerOnAudit(results[0]);
      message.success(`Encontrada: ${results[0].properties.farm_name}`);
    } else {
      message.info(`Se encontraron ${results.length} resultados`);
    }
  };

  // Find the audit_marker feature for a given audit id and centre
  // the map on it. Used by the bottom-table row click so the
  // planner can jump from the list to the corresponding marker on
  // the map. No-op when the audit has no geometry (and thus no
  // marker on the map).
  const centerOnAuditById = (auditId: number | null | undefined) => {
    if (!auditId || !mapData?.features) {
      message.info(
        t(
          "map.audit_has_no_geometry",
          "Esta auditoría no tiene geometría — no puede ubicarse en el mapa",
        ),
      );
      return;
    }
    const feature = (mapData.features as any[]).find(
      (f: any) =>
        f.properties?.layer_type === "audit_marker" &&
        f.properties?.audit_id === auditId,
    );
    if (!feature) {
      message.info(
        t(
          "map.audit_has_no_geometry",
          "Esta auditoría no tiene geometría — no puede ubicarse en el mapa",
        ),
      );
      return;
    }
    centerOnAudit(feature);
  };

  // Función para centrar el mapa en una auditoría
  const centerOnAudit = (feature: any) => {
    if (!mapInstance) return;

    const coords = feature.geometry.coordinates;
    mapInstance.setView([coords[1], coords[0]], 16);

    // Abrir el popup del marcador correspondiente
    if (markerClusterGroupRef.current) {
      markerClusterGroupRef.current.eachLayer((layer: any) => {
        const layerLatLng = layer.getLatLng();
        if (
          Math.abs(layerLatLng.lat - coords[1]) < 0.0001 &&
          Math.abs(layerLatLng.lng - coords[0]) < 0.0001
        ) {
          // Hacer zoom al cluster si es necesario
          markerClusterGroupRef.current.zoomToShowLayer(layer, () => {
            layer.openPopup();
          });
        }
      });
    }
  };

  // Crear marcadores con clusters cuando el mapa y los datos estén listos
  useEffect(() => {
    if (!mapInstance || !mapData || !mapData.features) return;

    // Limpiar cluster anterior si existe (esto destruye los popups
    // antiguos; también desmontamos sus roots React para liberar memoria).
    if (markerClusterGroupRef.current) {
      mapInstance.removeLayer(markerClusterGroupRef.current);
    }
    for (const root of popupRootsRef.current.values()) {
      try {
        root.unmount();
      } catch {
        // ignore
      }
    }
    popupRootsRef.current = new Map();
    markersByAuditIdRef.current = new Map();

    // Crear nuevo cluster group
    const markerClusterGroup = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    });

    // Filtrar solo los marcadores de auditorías que pasen los filtros
    // de estado / fecha del panel.
    const auditMarkers = mapData.features.filter(
      (f: any) =>
        f.properties.layer_type === "audit_marker" && passesFilters(f.properties),
    );

    // Función para obtener color según estado de auditoría — mapeado
    // al bucket normalizado (mismas etiquetas que las chips del filtro).
    const getMarkerColor = (props: any) => {
      const bucket = classifyAudit(props);
      const entry = STATE_BUCKETS.find((b) => b.value === bucket);
      return entry ? entry.color : "#d9d9d9";
    };

    // Función para generar el SVG del icono según el estado
    const getMarkerSVG = (color: string) => {
      // Determinar el color del borde exterior según el estado
      let outerColor = color;
      
      return `
        <svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <!-- Degradado para el borde exterior -->
            <linearGradient id="outerGradient-${color}" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${outerColor};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${outerColor};stop-opacity:0.8" />
            </linearGradient>
            <!-- Degradado para el círculo interior oscuro -->
            <radialGradient id="innerCircle">
              <stop offset="0%" style="stop-color:#6b1f3d;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#4a1529;stop-opacity:1" />
            </radialGradient>
            <!-- Degradado para la mandarina -->
            <radialGradient id="orangeGradient">
              <stop offset="0%" style="stop-color:#ff9933;stop-opacity:1" />
              <stop offset="70%" style="stop-color:#ff7700;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#ff6600;stop-opacity:1" />
            </radialGradient>
            <!-- Sombra -->
            <filter id="shadow">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
              <feOffset dx="0" dy="3" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.3"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          <!-- Sombra del marcador -->
          <ellipse cx="20" cy="47" rx="8" ry="2.5" fill="rgba(0,0,0,0.3)" filter="url(#shadow)"/>
          
          <!-- Marcador principal -->
          <g filter="url(#shadow)">
            <!-- Punta del marcador -->
            <path d="M 20 45 L 14 36 L 26 36 Z" fill="url(#outerGradient-${color})"/>
            
            <!-- Círculo exterior (borde de color) -->
            <circle cx="20" cy="20" r="18" fill="url(#outerGradient-${color})" stroke="white" stroke-width="1.5"/>
            
            <!-- Círculo interior oscuro (fondo morado) -->
            <circle cx="20" cy="20" r="14" fill="url(#innerCircle)"/>
            
            <!-- Mandarina -->
            <circle cx="20" cy="21" r="9" fill="url(#orangeGradient)" stroke="#ff6600" stroke-width="0.5"/>
            
            <!-- Brillo en la mandarina -->
            <ellipse cx="17" cy="18" rx="3.5" ry="4" fill="rgba(255,255,255,0.35)"/>
            
            <!-- Textura de piel (puntitos en diagonal) -->
            <circle cx="23" cy="23" r="0.7" fill="#ff9933" opacity="0.6"/>
            <circle cx="24" cy="25" r="0.6" fill="#ff9933" opacity="0.6"/>
            <circle cx="25" cy="27" r="0.5" fill="#ff9933" opacity="0.6"/>
            <circle cx="22" cy="26" r="0.5" fill="#ff9933" opacity="0.6"/>
            
            <!-- Sombra de la mandarina (lado derecho) -->
            <path d="M 25 15 Q 28 21 25 27 Q 27 24 27 21 Q 27 18 25 15 Z" fill="rgba(0,0,0,0.2)"/>
            
            <!-- Hoja verde -->
            <ellipse cx="16" cy="14" rx="4" ry="2.8" 
                     fill="#7cb342" 
                     stroke="#558b2f" 
                     stroke-width="0.8"
                     transform="rotate(-30 16 14)"/>
            
            <!-- Nervio de la hoja -->
            <line x1="15" y1="13" x2="17" y2="15" 
                  stroke="#558b2f" 
                  stroke-width="0.6" 
                  stroke-linecap="round"
                  transform="rotate(-30 16 14)"/>
            
            <!-- Tallo -->
            <line x1="18" y1="14" x2="20" y2="12.5" 
                  stroke="#6d4c41" 
                  stroke-width="1.2" 
                  stroke-linecap="round"/>
          </g>
        </svg>
      `;
    };

    // Crear marcadores
    auditMarkers.forEach((feature: any) => {
      const coords = feature.geometry.coordinates;
      const props = feature.properties;
      const color = getMarkerColor(props);

      // Crear icono personalizado con SVG
      const icon = L.divIcon({
        className: "custom-marker-orange",
        html: getMarkerSVG(color),
        iconSize: [40, 50],
        iconAnchor: [20, 47],
        popupAnchor: [0, -47],
      });

      const marker = L.marker([coords[1], coords[0]], { icon });

      // Popup React-rendered: contiene la info actual + click-to-call
      // + DatePicker inline para planificar la auditoría. Cada marker
      // recibe su propio nodo + root porque el cluster los reusa.
      const popupNode = document.createElement("div");
      popupNode.style.minWidth = "260px";
      const root = createRoot(popupNode);
      root.render(
        <AuditMarkerPopup
          props={props}
          onPlan={(d) => updatePlanningDate(props.audit_id, d)}
          onOpenDetail={() => navigate(`/audits/${props.audit_id}`)}
          onDownloadBundle={() => downloadBundleFromPopup(props)}
          t={t as any}
        />,
      );
      popupRootsRef.current.set(props.audit_id, root);
      markersByAuditIdRef.current.set(props.audit_id, marker);
      marker.bindPopup(popupNode, {
        minWidth: 260,
        // Re-bind the same node, so clicking the same marker after a
        // save re-uses the React tree (preserves draft state).
        autoClose: false,
      });

      markerClusterGroup.addLayer(marker);
    });

    // Agregar cluster al mapa
    mapInstance.addLayer(markerClusterGroup);
    markerClusterGroupRef.current = markerClusterGroup;

    // Auto-fit policy: re-centre when the marker set changes due to
    // a *filter* mutation (initial mount included).
    //
    // We compute the bounds ourselves from the geographic marker
    // positions instead of relying on `markerClusterGroup.getBounds()`,
    // and we explicitly exclude every non-audit-marker feature. Two
    // reasons:
    //   1. The previous version used the cluster's own bounds —
    //      that's already markers-only mathematically, but it left
    //      doubt about whether route polylines (added to a separate
    //      `routesLayer`) were somehow pulling the camera. By
    //      computing the bbox from the latlngs we just placed we
    //      can prove the routes are out of the calculation.
    //   2. We cap with `maxZoom` so the camera never zooms out to
    //      "see the whole country" when one audit is geographically
    //      far from the rest — that scenario reads like "the routes
    //      are being included" to the planner.
    //
    // Popup saves rebuild the cluster too, but they preserve the
    // filter signature, so they won't trigger a re-fit.
    const filterSignature = JSON.stringify({
      states: Array.from(stateFilters).sort(),
      from: effectiveDateRange?.[0]?.toISOString() ?? null,
      to: effectiveDateRange?.[1]?.toISOString() ?? null,
    });
    const shouldFit =
      !didInitialFitRef.current ||
      filterSignature !== lastFitFilterSignatureRef.current;
    if (shouldFit && auditMarkers.length > 0) {
      // Build the bbox strictly from `audit_marker` Point coords
      // — same payload that became cluster markers above.
      const latLngs: L.LatLngTuple[] = [];
      for (const f of auditMarkers) {
        const coords = (f as any)?.geometry?.coordinates;
        if (Array.isArray(coords) && coords.length >= 2) {
          const lng = Number(coords[0]);
          const lat = Number(coords[1]);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            latLngs.push([lat, lng]);
          }
        }
      }
      if (latLngs.length > 0) {
        const bounds = L.latLngBounds(latLngs);
        if (bounds.isValid()) {
          mapInstance.fitBounds(bounds, {
            padding: [50, 50],
            // Keep the camera within "this is a planning view"
            // territory even when audits are scattered. Without it
            // the fitBounds collapses to country-level zoom and
            // the polylines drawn by the routesLayer happen to
            // fall inside the viewport — which is what looked
            // like "centering is using the routes".
            maxZoom: 14,
          });
          didInitialFitRef.current = true;
          lastFitFilterSignatureRef.current = filterSignature;
        }
      }
    } else if (shouldFit) {
      // Filters dropped every marker — at least remember the
      // signature so we don't keep trying to fit to nothing.
      lastFitFilterSignatureRef.current = filterSignature;
    }

    // Reabrir el popup de la auditoría que el coordinador acababa de
    // guardar. El rebuild del cluster cierra cualquier popup abierto;
    // sin esto la UX sería "doy a guardar y se cierra todo".
    const pendingId = pendingPopupAuditIdRef.current;
    if (pendingId != null) {
      pendingPopupAuditIdRef.current = null;
      const marker = markersByAuditIdRef.current.get(pendingId);
      if (marker) {
        // El marker puede estar agrupado en un cluster — pedimos al
        // cluster que lo descubra antes de abrir el popup. Si ya está
        // visible, el callback se llama inmediatamente.
        try {
          markerClusterGroup.zoomToShowLayer(marker, () => {
            marker.openPopup();
          });
        } catch {
          marker.openPopup();
        }
      }
    }
  }, [mapInstance, mapData, stateFilters, effectiveDateRange]);

  // Visualizar rutas (tracks) en el mapa solo cuando zoom > 12
  useEffect(() => {
    if (!mapInstance || !mapData || !mapData.features) return;

    // Solo mostrar rutas si zoom > 12
    if (currentZoom > 12) {
      // Limpiar capa de rutas anterior si existe
      if (routesLayerRef.current) {
        mapInstance.removeLayer(routesLayerRef.current);
      }

      // Crear nueva capa de rutas
      const routesLayer = L.layerGroup();

      // Filtrar solo las rutas
      const routes = mapData.features.filter(
        (f: any) => f.properties.layer_type === "route"
      );

      // Colores para diferentes auditores
      const auditorColors: { [key: string]: string } = {};
      const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96'];
      let colorIndex = 0;

      routes.forEach((feature: any) => {
        try {
          const props = feature.properties;
          
          // Asignar color por auditor
          if (!auditorColors[props.auditor]) {
            auditorColors[props.auditor] = colors[colorIndex % colors.length];
            colorIndex++;
          }

          const geoJsonLayer = L.geoJSON(feature, {
            style: {
              color: auditorColors[props.auditor],
              weight: 3,
              opacity: 0.7,
            },
            onEachFeature: (feat, layer) => {
              const routeProps = feat.properties;
              
              // Formatear fecha
              let formattedDate = routeProps.date;
              try {
                const date = new Date(routeProps.date);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                formattedDate = `${day}/${month}/${year}`;
              } catch (e) {
                // Mantener fecha original si falla el formateo
              }

              layer.bindPopup(`
                <div style="font-family: Arial, sans-serif; min-width: 200px;">
                  <strong style="font-size: 14px; color: ${auditorColors[routeProps.auditor]};">🚗 Ruta GPS</strong><br/>
                  <hr style="margin: 5px 0; border: none; border-top: 1px solid #ddd;"/>
                  <strong>Auditor:</strong> ${routeProps.auditor}<br/>
                  <strong>Código:</strong> ${routeProps.route_code}<br/>
                  <strong>Fecha:</strong> ${formattedDate}<br/>
                  <strong>Distancia:</strong> ${Number(routeProps.distance).toFixed(2)} km
                </div>
              `);
            },
          });

          routesLayer.addLayer(geoJsonLayer);
        } catch (error) {
          // Error handled silently
        }
      });

      // Agregar capa al mapa
      if (routes.length > 0) {
        mapInstance.addLayer(routesLayer);
        routesLayerRef.current = routesLayer;
      }
    } else {
      // Si zoom <= 12, remover rutas
      if (routesLayerRef.current) {
        mapInstance.removeLayer(routesLayerRef.current);
        routesLayerRef.current = null;
      }
    }

  }, [mapInstance, mapData, currentZoom]);

  // Listener de zoom para cargar parcelas dinámicamente
  useEffect(() => {
    if (!mapInstance || !mapData) return;

    const ZOOM_THRESHOLD = 13;

    // Render once and toggle layer visibility on zoom — that's much
    // simpler than rebuilding the GeoJSON layer on every pan/zoom and
    // sidesteps the viewport-intersects bug that was hiding parcels.
    const plotsLayer = L.layerGroup();

    // Which audit ids should be visible right now. Polygons whose
    // `audit_id` isn't in this set are hidden so the parcels follow the
    // same state/date filters as the markers.
    const visibleAuditIds = new Set<number>(
      (mapData.features as any[])
        .filter(
          (f) =>
            f.properties.layer_type === "audit_marker" &&
            passesFilters(f.properties),
        )
        .map((f) => f.properties.audit_id),
    );

    // 1) Geometría de la finca (Plot — declared by the producer).
    //    Rendered first so audit polygons sit on top. Dashed grey
    //    outline + faint fill makes it clearly a "background" reference.
    const initialPlots = (mapData.features as any[]).filter(
      (f) =>
        f.properties.layer_type === "initial_plot" &&
        visibleAuditIds.has(f.properties.audit_id),
    );
    initialPlots.forEach((feature: any) => {
      try {
        const geoJsonLayer = L.geoJSON(feature, {
          style: {
            color: "#595959",
            weight: 1.5,
            dashArray: "4 4",
            fillColor: "#bfbfbf",
            fillOpacity: 0.1,
          },
          onEachFeature: (feat, layer) => {
            const props = feat.properties;
            layer.bindPopup(`
              <div style="font-family: Arial, sans-serif; min-width: 220px;">
                <strong style="font-size: 14px; color: #000;">📐 ${t("map.farm_geometry", "Geometría finca")}</strong><br/>
                <hr style="margin: 5px 0; border: none; border-top: 1px solid #ddd;"/>
                <strong>${t("audit.producer", "Productor")}:</strong> ${props.producer_name}<br/>
                <strong>${t("audit.farm", "Finca")}:</strong> ${props.farm_name}<br/>
                <strong>${t("audit.reference", "Referencia")}:</strong> ${props.audit_reference}<br/>
                <strong>${t("audit.surface", "Superficie")}:</strong> ${props.surface?.toFixed(2) || 0} ha
              </div>
            `);
          },
        });
        plotsLayer.addLayer(geoJsonLayer);
      } catch {
        // Skip plots with malformed geometry
      }
    });

    // 2) Geometría de la auditoría (PlotReal — drawn by the auditor).
    const finalPlots = (mapData.features as any[]).filter(
      (f) =>
        f.properties.layer_type === "final_plot" &&
        visibleAuditIds.has(f.properties.audit_id),
    );
    finalPlots.forEach((feature: any) => {
      try {
        const geoJsonLayer = L.geoJSON(feature, {
          style: {
            color: "#ff8c00",
            weight: 2,
            fillOpacity: 0.3,
          },
          onEachFeature: (feat, layer) => {
            const props = feat.properties;
            const hasProduction =
              props.production_estimation !== null &&
              props.production_estimation !== undefined;
            layer.bindPopup(`
              <div style="font-family: Arial, sans-serif; min-width: 220px;">
                <strong style="font-size: 14px; color: #000;">🌱 ${props.plot_name || 'Parcela sin nombre'}</strong><br/>
                <hr style="margin: 5px 0; border: none; border-top: 1px solid #ddd;"/>
                <strong>Productor:</strong> ${props.producer_name}<br/>
                <strong>Finca:</strong> ${props.farm_name}<br/>
                <strong>Superficie:</strong> ${props.plot_surface?.toFixed(2) || 0} ha<br/>
                <strong>Marco plantación:</strong> ${props.plantation_frame || 'N/A'}<br/>
                <strong>Patrón:</strong> ${props.rootstock || 'N/A'}${hasProduction ? `<br/><strong>Estimación producción:</strong> ${Number(props.production_estimation).toLocaleString('es-ES')} kg` : ''}
              </div>
            `);
          },
        });
        plotsLayer.addLayer(geoJsonLayer);
      } catch {
        // Error handled silently
      }
    });

    const applyZoomVisibility = () => {
      const zoom = mapInstance.getZoom();
      setCurrentZoom(zoom);
      const shouldShow = zoom >= ZOOM_THRESHOLD;
      const hasLayer = mapInstance.hasLayer(plotsLayer);
      if (shouldShow && !hasLayer) {
        mapInstance.addLayer(plotsLayer);
      } else if (!shouldShow && hasLayer) {
        mapInstance.removeLayer(plotsLayer);
      }
    };

    // Initial state + listener (zoomend covers both wheel and double-click)
    applyZoomVisibility();
    mapInstance.on("zoomend", applyZoomVisibility);

    plotsLayerRef.current = plotsLayer;

    const handleZoomEnd = applyZoomVisibility;

    return () => {
      mapInstance.off("zoomend", handleZoomEnd);
      if (mapInstance.hasLayer(plotsLayer)) {
        mapInstance.removeLayer(plotsLayer);
      }
    };
  }, [mapInstance, mapData, t, stateFilters, effectiveDateRange]);

  if (isPlanningLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" tip={t("common.loading", "Cargando...")} />
      </div>
    );
  }

  // Custom breadcrumb
  const breadcrumb = (
    <Breadcrumb
      items={[
        {
          title: (
            <Link to="/">
              <HomeOutlined /> {t("home.title", "Inicio")}
            </Link>
          ),
        },
        {
          title: (
            <Link to="/planning-groups">
              <CalendarOutlined /> {t("planning.title", "Planificaciones")}
            </Link>
          ),
        },
        {
          title: <Link to={`/planning-groups/show/${id}`}>{planningData?.data?.name || id}</Link>,
        },
        {
          title: (
            <>
              <EnvironmentOutlined /> {t("map.title", "Vista de Mapa")}
            </>
          ),
        },
      ]}
    />
  );

  return (
    <Show
      isLoading={isPlanningLoading}
      title={`${t("map.title")}: ${planningData?.data?.name || ""}`}
      breadcrumb={breadcrumb}
      headerButtons={() => (
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/planning-groups/show/${id}`)}
        >
          {t("common.back", "Volver")}
        </Button>
      )}
    >
      <Card style={{ position: 'relative' }}>
        {/* Spinner overlay mientras se cargan los datos del mapa */}
        {isMapDataLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              borderRadius: '8px',
            }}
          >
            <Spin size="large" tip={t("map.loading_data", "Cargando datos del mapa...")} />
          </div>
        )}
        
        {/* Panel de planificación: filtros por estado + rango de fechas.
            Permite al coordinador / auditor ocultar auditorías que no le
            interesan en este momento y centrarse en lo que tiene que
            visitar este día / semana / mes. */}
        <Card
          size="small"
          title={t("map.planner_title", "Planificador de visitas")}
          style={{ marginBottom: 12, backgroundColor: "#fafafa" }}
          extra={
            <Button
              size="small"
              type="text"
              icon={<ClearOutlined />}
              onClick={() => {
                setStateFilters(new Set(STATE_BUCKETS.map((b) => b.value)));
                setDateRange(null);
                setQuickRange("all");
              }}
            >
              {t("common.clearFilters", "Limpiar filtros")}
            </Button>
          }
        >
          <Space wrap size="middle" align="center">
            <Space wrap size={6}>
              {STATE_BUCKETS.map((b) => {
                const active = stateFilters.has(b.value);
                return (
                  <Tag.CheckableTag
                    key={b.value}
                    checked={active}
                    onChange={(checked) => {
                      setStateFilters((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(b.value);
                        else next.delete(b.value);
                        return next;
                      });
                    }}
                    style={{
                      borderLeft: `4px solid ${b.color}`,
                      paddingInline: 10,
                    }}
                  >
                    {b.label}
                  </Tag.CheckableTag>
                );
              })}
            </Space>

            <Segmented
              size="small"
              value={quickRange}
              onChange={(v) => {
                setQuickRange(v as typeof quickRange);
                // Quick range overrides the explicit picker — clear it
                // so the user sees a coherent state.
                if (v !== "all") setDateRange(null);
              }}
              options={[
                { label: t("map.range_all", "Todas"), value: "all" },
                { label: t("map.range_today", "Hoy"), value: "today" },
                { label: t("map.range_week", "Semana"), value: "week" },
                { label: t("map.range_month", "Mes"), value: "month" },
              ]}
            />

            <RangePicker
              size="small"
              format="DD/MM/YYYY"
              value={dateRange ?? undefined}
              onChange={(range) => {
                if (range && range[0] && range[1]) {
                  setDateRange([range[0], range[1]] as [Dayjs, Dayjs]);
                  setQuickRange("all");
                } else {
                  setDateRange(null);
                }
              }}
              placeholder={[
                t("map.range_from", "Desde"),
                t("map.range_to", "Hasta"),
              ]}
            />
          </Space>
        </Card>

        {/* Buscador de auditorías */}
        <div style={{ marginBottom: 16, width: "40%" }}>
          <Card
            size="small"
            title="🔍 Buscar Auditoría"
            style={{ backgroundColor: "#fafafa" }}
          >
            <Search
              placeholder="Buscar por productor, finca o referencia..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={handleSearch}
              enterButton={<SearchOutlined />}
              loading={isMapDataLoading}
              size="middle"
            />
            
            {/* Lista de resultados si hay múltiples */}
            {searchResults.length > 1 && (
              <div style={{ marginTop: 12 }}>
                <strong>Resultados encontrados ({searchResults.length}):</strong>
                <div style={{ maxHeight: 150, overflowY: 'auto', marginTop: 8 }}>
                  {searchResults.map((result: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        padding: '8px 12px',
                        marginBottom: 4,
                        backgroundColor: 'white',
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e6f7ff';
                        e.currentTarget.style.borderColor = '#1890ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#d9d9d9';
                      }}
                      onClick={() => {
                        centerOnAudit(result);
                        message.success(`Centrado en: ${result.properties.farm_name}`);
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>
                        🏭 {result.properties.farm_name}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        Productor: {result.properties.producer_name} | Ref: {result.properties.audit_reference}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        <CustomMap
          height="700px"
          showAddressSearch={true}
          showSearchBar={isSpain}
          showLayerControl={true}
          onMapReady={(map) => setMapInstance(map)}
        />

        {/* Planning-aid table — every audit in the planning, with
            search + click-to-centre + per-row "assign auditor /
            plan date" action. Replaces the older
            "missing_geometries" panel. */}
        {(() => {
          // Source of truth: `all_audits` (new). Fallback to the
          // legacy `missing_geometries` field so this page keeps
          // working against an older backend.
          const allAudits: any[] = Array.isArray(mapData?.all_audits)
            ? mapData.all_audits
            : Array.isArray(mapData?.missing_geometries)
              ? mapData.missing_geometries
              : [];
          if (allAudits.length === 0) return null;
          const q = tableSearch.trim().toLowerCase();
          const filtered = q
            ? allAudits.filter((r) => {
                return (
                  (r.audit_reference || "").toLowerCase().includes(q) ||
                  (r.producer_name || "").toLowerCase().includes(q) ||
                  (r.farm_name || "").toLowerCase().includes(q)
                );
              })
            : allAudits;
          return (
            <Collapse
              style={{ marginTop: 16 }}
              defaultActiveKey={["planner"]}
              items={[
                {
                  key: "planner",
                  label: (
                    <Space>
                      <CalendarOutlined style={{ color: "#1677ff" }} />
                      <strong>
                        {t(
                          "map.planning_table_title",
                          "Auditorías de esta planificación",
                        )}
                      </strong>
                      <Tag color="blue">{allAudits.length}</Tag>
                    </Space>
                  ),
                  extra: (
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={isMapDataLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchMapData();
                      }}
                    >
                      {t("common.refresh", "Actualizar")}
                    </Button>
                  ),
                  children: (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={t(
                          "map.planning_table_help",
                          "Listado de ayuda a la planificación diaria. Pulsa una fila para centrar el mapa en esa auditoría, o usa la acción para asignar auditor y fecha planificada.",
                        )}
                      />
                      <Input
                        placeholder={t(
                          "map.planning_table_search",
                          "Buscar por referencia, productor o finca…",
                        )}
                        prefix={<SearchOutlined />}
                        allowClear
                        value={tableSearch}
                        onChange={(e) => setTableSearch(e.target.value)}
                        style={{ marginBottom: 12, maxWidth: 420 }}
                      />
                      <Table
                        size="small"
                        dataSource={filtered}
                        rowKey="audit_id"
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        onRow={(record: any) => ({
                          onClick: () => centerOnAuditById(record.audit_id),
                          style: {
                            cursor: record.has_any_geometry
                              ? "pointer"
                              : "default",
                          },
                        })}
                      >
                        <Table.Column
                          title={t("audit.reference", "Referencia")}
                          dataIndex="audit_reference"
                          width={130}
                          render={(value: string, record: any) => (
                            <Link
                              to={`/audits/${record.audit_id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {value || "—"}
                            </Link>
                          )}
                        />
                        <Table.Column
                          title={t("audit.producer", "Productor")}
                          dataIndex="producer_name"
                          render={(v: string | null) => v || "—"}
                          sorter={(a: any, b: any) =>
                            (a.producer_name || "").localeCompare(
                              b.producer_name || "",
                            )
                          }
                        />
                        <Table.Column
                          title={t("audit.farm", "Finca")}
                          dataIndex="farm_name"
                          render={(v: string | null) => v || "—"}
                          sorter={(a: any, b: any) =>
                            (a.farm_name || "").localeCompare(
                              b.farm_name || "",
                            )
                          }
                        />
                        <Table.Column
                          title={t("audit.surface", "Superficie (ha)")}
                          dataIndex="surface"
                          width={120}
                          align="right"
                          sorter={(a: any, b: any) =>
                            (a.surface || 0) - (b.surface || 0)
                          }
                          render={(v: number | null | undefined) =>
                            (v ?? 0).toFixed(2)
                          }
                        />
                        <Table.Column
                          title={t("audit.auditor", "Auditor")}
                          dataIndex="auditor"
                          render={(v: string | null) => v || "—"}
                          sorter={(a: any, b: any) =>
                            (a.auditor || "").localeCompare(b.auditor || "")
                          }
                        />
                        <Table.Column
                          title={t("audit.state", "Estado")}
                          dataIndex="audit_state"
                          width={130}
                          render={(v: string | null) => v || "—"}
                          sorter={(a: any, b: any) =>
                            (a.audit_state || "").localeCompare(
                              b.audit_state || "",
                            )
                          }
                        />
                        <Table.Column
                          title={t(
                            "audit.planning_date",
                            "Fecha planificada",
                          )}
                          dataIndex="planning_date"
                          width={140}
                          align="center"
                          sorter={(a: any, b: any) => {
                            const av = a.planning_date
                              ? new Date(a.planning_date).getTime()
                              : 0;
                            const bv = b.planning_date
                              ? new Date(b.planning_date).getTime()
                              : 0;
                            return av - bv;
                          }}
                          render={(v: string | null) =>
                            v ? dayjs(v).format("DD/MM/YYYY") : "—"
                          }
                        />
                        <Table.Column
                          // Merged "Geometría finca/auditoría". The
                          // distinction between the two layers is
                          // useful in the data-quality view but
                          // overkill for the daily planner — what
                          // matters here is whether the audit has
                          // anything to centre on at all.
                          title={t("map.geometry_short", "Geometría")}
                          dataIndex="has_any_geometry"
                          width={110}
                          align="center"
                          sorter={(a: any, b: any) =>
                            Number(b.has_any_geometry || false) -
                            Number(a.has_any_geometry || false)
                          }
                          render={(_v: boolean | undefined, record: any) => {
                            const hasGeom =
                              record.has_any_geometry ??
                              record.has_farm_geometry ??
                              record.has_audit_geometry;
                            return hasGeom ? (
                              <Tag icon={<CheckCircleOutlined />} color="success">
                                {t("common.yes", "Sí")}
                              </Tag>
                            ) : (
                              <Tag icon={<CloseCircleOutlined />} color="error">
                                {t("common.no", "No")}
                              </Tag>
                            );
                          }}
                        />
                        <Table.Column
                          title={t("common.actions", "Acciones")}
                          key="actions"
                          width={140}
                          align="center"
                          onCell={() => ({
                            // Don't trigger the row's centre-on-map
                            // navigation when the user clicks an
                            // action button.
                            onClick: (e) => e.stopPropagation(),
                          })}
                          render={(_: unknown, record: any) => (
                            <Space size="small">
                              <Button
                                size="small"
                                type="text"
                                icon={<AimOutlined />}
                                title={t(
                                  "map.center_on_audit",
                                  "Centrar en el mapa",
                                )}
                                disabled={!record.has_any_geometry}
                                onClick={() =>
                                  centerOnAuditById(record.audit_id)
                                }
                              />
                              {isAdmin && (
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<UserOutlined />}
                                  title={t(
                                    "map.assign_auditor_short",
                                    "Asignar auditor / fecha",
                                  )}
                                  onClick={() =>
                                    setAssignAuditorAudit({
                                      // Synthesize an Audit-shaped
                                      // object for the modal. Only
                                      // the four fields below are
                                      // actually read by the modal.
                                      id: record.audit_id,
                                      auditor_user: record.auditor_user_id,
                                      auditor_name: record.auditor,
                                      planning_date: record.planning_date,
                                    })
                                  }
                                />
                              )}
                              {isAdmin && record.producer_id && (
                                <Button
                                  size="small"
                                  type="text"
                                  href={`/producers/show/${record.producer_id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  icon={<ExportOutlined />}
                                  title={t(
                                    "map.open_producer_detail",
                                    "Abrir detalle del productor",
                                  )}
                                />
                              )}
                            </Space>
                          )}
                        />
                      </Table>
                    </>
                  ),
                },
              ]}
            />
          );
        })()}

        {/* Inline assign-auditor modal — opened from the planner
            table's per-row action. Pulls candidate auditors from
            /app-users itself (see AssignAuditorModal internals);
            we only need to pass the planning's audit_company /
            country to filter the dropdown. */}
        {assignAuditorAudit && planningData?.data && (
          <AssignAuditorModal
            visible={!!assignAuditorAudit}
            onClose={() => setAssignAuditorAudit(null)}
            audit={assignAuditorAudit as any}
            auditors={[]}
            auditCompanyId={planningData.data.audit_company ?? null}
            countryId={
              (planningData.data as any).country?.id ??
              (planningData.data as any).season?.country?.id ??
              null
            }
            onSuccess={() => {
              setAssignAuditorAudit(null);
              fetchMapData();
            }}
          />
        )}
      </Card>
    </Show>
  );
};
